import { Box, Table, TableBody, TableCell, TableRow, Typography } from "@material-ui/core";
import { useAsync } from "@hooks";
import { entitiesService } from "@services";
import React, { useMemo } from "react";
import { parseCsvImportError } from "@modules/CsvImportErrorParser";

type Props = { error: string; entityTypeId: number };

const formatValue = (value: unknown): string => {
    if (value === null || value === undefined) return "—";
    if (Array.isArray(value)) return value.length ? value.join(", ") : "—";
    if (typeof value === "string") return value;
    return JSON.stringify(value);
};

/**
 * Читаемое представление ошибки CSV-импорта вместо сырого текста Postgres-
 * исключения с числовыми atype_id (см. CsvImportErrorParser.ts). Имя поля
 * core с 30.09.2026 кладёт в само сообщение — схему (/nsi_meta/type_schema)
 * запрашиваем только как fallback, если core ещё не обновлён.
 */
const ImportCsvErrorDetails = ({ error, entityTypeId }: Props) => {
    const parsed = useMemo(() => parseCsvImportError(error), [error]);

    const needsSchemaFallback =
        !!parsed &&
        (parsed.kind === "alias_not_found"
            ? !parsed.attributeName
            : parsed.attrs.some((attr) => !attr.attributeName));

    const { data: schema } = useAsync(entitiesService.getTypeSchema, {
        functionArgs: [entityTypeId],
        canLoad: needsSchemaFallback && Boolean(entityTypeId),
        setLoadingStatusOnUpdate: false,
    });

    const nameById = useMemo(() => {
        const map = new Map<number, string>();
        (schema || []).forEach((attr) => map.set(attr.at_id, attr.a_name));
        return map;
    }, [schema]);

    if (!parsed) {
        return <Typography variant="body2">{error}</Typography>;
    }

    if (parsed.kind === "alias_not_found") {
        const fieldName = parsed.attributeName ?? nameById.get(parsed.atypeId) ?? `атрибут #${parsed.atypeId}`;
        return (
            <Typography variant="body2">
                Значение «{parsed.alias}» не найдено в справочнике поля «{fieldName}» — заведите его в
                соответствующем справочнике перед повторной загрузкой.
            </Typography>
        );
    }

    return (
        <Box>
            <Typography variant="body2" gutterBottom>
                {parsed.summary}
            </Typography>
            <Table size="small">
                <TableBody>
                    {parsed.attrs.map((attr) => (
                        <TableRow key={attr.atypeId}>
                            <TableCell style={{ whiteSpace: "nowrap", fontWeight: 500, paddingLeft: 0 }}>
                                {attr.attributeName ?? nameById.get(attr.atypeId) ?? `#${attr.atypeId}`}
                            </TableCell>
                            <TableCell>{formatValue(attr.value)}</TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </Box>
    );
};

export default ImportCsvErrorDetails;
